import { Alert, Button } from '@cloudscape-design/components';
import React from 'react';
import { useNavigate } from 'react-router';
import { PAGE } from 'src/pages/enums';
import {
    isUnreadForUser,
    useRecentlyUpdatedList,
    useUpdatesReadState,
} from 'src/hooks/useRecentlyUpdated';
import { useTranslate } from 'src/i18n/useTranslate';

/**
 * Personal_Updates_Banner — Slice 4 of the Recently_Updated_Notifications
 * feature. Rendered at the top of the home page's `Content` region above
 * the "Recently viewed" / "Favorites" blocks (design.md
 * § PersonalUpdatesBanner component).
 *
 * Visibility rule (requirement 6.2): the banner renders only when the
 * personal list has at least one entry AND the newest entry's
 * `latestChangeAt` is strictly newer than the user's `lastViewedAt`
 * (or the user has never visited the Recently_Updated_Page). Fetches
 * `useRecentlyUpdatedList('personal', 1)` and `useUpdatesReadState()`
 * on mount — the backend caps `limit` at 100 but we only need the newest
 * item for the comparison.
 *
 * Failure handling (requirement 6.5): a rejected fetch resolves to
 * `data: undefined` inside the hook, which flows through the same
 * `data.length > 0` guard — the banner simply does not render. No user-
 * visible error UI is produced. The hook already `console.error`s the
 * fetch failure with request context.
 *
 * The Alert is intentionally NOT `dismissible` (requirement 6.4): the
 * banner clears implicitly on the next home-page load after the user
 * visits `/recently-updated`, which calls `markRead` and bumps
 * `lastViewedAt` past the newest personal entry. There is no X.
 */

export const BANNER_DESTINATION = `/${PAGE.RECENTLY_UPDATED}`;

/**
 * Pure visibility predicate. Separated from the component so unit tests
 * can drive the three visibility branches (empty, stale, unread) without
 * mounting React or stubbing the shared data hooks.
 *
 * `latestChangeAt` is an ISO-8601 UTC string from the backend;
 * `lastViewedAt` is epoch-ms (matching the sibling `firstLoginTimestamp`
 * / `tutorialLaunchTimestamp` attributes on the same user profile row,
 * see requirements § Updates_Read_State_Record). Compare both in the
 * epoch-ms domain so a parse error on the ISO string surfaces as "not
 * unread" rather than as a crash.
 */
export const isPersonalUnread = (
    latestChangeAt: string | undefined,
    lastViewedAt: number | null
): boolean =>
    isUnreadForUser(
        latestChangeAt ? [{ latestChangeAt }] : undefined,
        lastViewedAt
    );

export const PersonalUpdatesBanner: React.FC = () => {
    const navigate = useNavigate();
    const t = useTranslate();
    const { data: personal } = useRecentlyUpdatedList('personal', 1);
    const { lastViewedAt } = useUpdatesReadState();

    // Requirement 6.2 / 6.5: a fetch failure leaves `personal` undefined,
    // which falls through the same guard as an empty list — the banner
    // never surfaces an error state.
    if (!personal || personal.length === 0) {
        return null;
    }
    if (!isPersonalUnread(personal[0].latestChangeAt, lastViewedAt)) {
        return null;
    }

    return (
        <Alert
            type="info"
            header={t(
                'personal-updates-banner-header',
                'Updates to your documents'
            )}
            data-testid="personal-updates-banner"
            action={
                <Button
                    data-testid="personal-updates-banner-action"
                    onClick={() => navigate(BANNER_DESTINATION)}
                >
                    {t('personal-updates-banner-action', "See what's changed")}
                </Button>
            }
        >
            {t(
                'personal-updates-banner-body',
                'One or more of your favorited or recently-viewed documents has been updated in the last 30 days.'
            )}
        </Alert>
    );
};

export default PersonalUpdatesBanner;
